import React, { useMemo } from 'react'
import {
  ContainsEditableProps,
  useEditor,
  FieldType
} from '@toasttab/sites-components'
import { Block as BlockType } from 'src/apollo/sites'
import LocationMap from 'public/components/default_template/schedule_map/LocationMap'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { formatLocationAddress, sortLocations } from 'shared/utils/addressUtils'
import { t } from 'src/translations'
import './editable_map.css'

type MapComponentProps = {
  block?: BlockType
  inlineStyles?: React.CSSProperties
  isEditable?: boolean
}

type EditableMapComponentProps = {
  block?: BlockType
} & ContainsEditableProps

export type MapProps = {
  block?: BlockType
  editPath: string
  styles?: React.CSSProperties
}

const MapComponent = React.forwardRef<HTMLDivElement, MapComponentProps>(
  ({ block, inlineStyles, isEditable = false }, ref) => {
    const { locations } = useRestaurant()
    const locationId = block?.contents?.locationId

    const sortedLocations = useMemo(() => sortLocations(locations), [locations])

    const displayLocation = useMemo(() => {
      if (!sortedLocations || sortedLocations.length === 0) {
        return null
      }

      if (!locationId || locationId === '') {
        return sortedLocations[0]
      }

      return (
        sortedLocations.find((loc) => loc?.externalId === locationId) ||
        sortedLocations[0]
      )
    }, [locationId, sortedLocations])

    const mapProps = useMemo(
      () => ({
        name: displayLocation?.name ?? '',
        address1: displayLocation?.address1 ?? '',
        address2: displayLocation?.address2 ?? null,
        city: displayLocation?.city ?? '',
        state: displayLocation?.state ?? '',
        zipcode: displayLocation?.zipcode ?? ''
      }),
      [displayLocation]
    )

    return (
      <div
        ref={ref}
        className={isEditable ? 'editableMapContainer' : undefined}
        style={inlineStyles}
        data-testid='editable-map-wrapper'
      >
        <LocationMap
          {...mapProps}
          alwaysShowMap={true}
          wrapperClassName='dynamicMapWrapper'
          trackerContext='map_component'
        />
      </div>
    )
  }
)

const EditableMapComponent: React.FC<EditableMapComponentProps> = ({
  editPath,
  block
}) => {
  const { useEditableRef } = useEditor()
  const { locations } = useRestaurant()

  const sortedLocations = useMemo(() => sortLocations(locations), [locations])

  const locationEnums = useMemo(() => {
    const enums: { displayName: string; value: string }[] = []

    if (sortedLocations && sortedLocations.length > 0) {
      sortedLocations.forEach((location) => {
        if (location?.externalId) {
          const locationAddress = formatLocationAddress(location)
          if (locationAddress) {
            enums.push({
              displayName: locationAddress,
              value: location.externalId
            })
          }
        }
      })
    }

    return enums
  }, [sortedLocations])

  const currentValue =
    block?.contents?.locationId ?? sortedLocations[0]?.externalId ?? ''

  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'map',
    displayName: 'Map',
    actions: ['delete'],
    path: editPath,
    schema: {
      fields: [
        {
          path: `${editPath}.locationId`,
          displayName: 'Map Location',
          type: FieldType.Enum,
          value: currentValue,
          enums: locationEnums,
          helperText: t('editable-map.location-selector-helper')
        }
      ]
    }
  })

  return <MapComponent ref={editableRef} block={block} isEditable />
}

const EditableMap: React.FC<MapProps> = ({ block, editPath, styles = {} }) => {
  const { isEditor } = useEditor()

  return isEditor ? (
    <EditableMapComponent editPath={editPath} block={block} />
  ) : (
    <MapComponent block={block} inlineStyles={styles} />
  )
}

export default EditableMap
